import { useCallback } from 'react';
import { ActionEventArgs } from '@syncfusion/ej2-react-schedule';
import { useDispatch } from 'react-redux';
import intl from 'react-intl-universal';
import get from 'lodash/get';
import { EventData, isSingleDayEventType } from 'utils/types/learningSchedule';
import {
  createEvent,
  editEvent,
  deleteEvent,
  createException,
} from 'state/Schedule/ScheduleSlice';
import { showNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import {
  getEventTypeValueFromCode,
  processEventTimesForEdit,
  processEventTimesForCreate,
  generateReadableRecurrancePattern,
  getEventData,
  isSingleDayAllDayEvent,
} from '../helpers/helpers';
import {
  serializeCreateEvent,
  serializeUpdateEvent,
  serializeCreateException,
} from '../helpers/helpers';

interface UseScheduleActionsProps {
  eventTimezones: boolean;
  projectIds: string[];
  eventTitle: string;
  eventDescription: string;
  eventType: string;
  selectedFacilitatorsIds: string[];
  isEditingSeriesWithExceptions: boolean;
  resetStateHandler?: () => void;
  scheduleObj?: React.RefObject<any>;
}

export const useScheduleActions = ({
  eventTimezones,
  projectIds,
  eventTitle,
  eventDescription,
  eventType,
  selectedFacilitatorsIds,
  isEditingSeriesWithExceptions,
  resetStateHandler,
  scheduleObj,
}: UseScheduleActionsProps) => {
  const dispatch = useDispatch();
  const dispatchErrorNotification = useCallback(
    (notificationTitle: string) => {
      dispatch(
        showNotification({
          notificationVariant: 'error',
          notificationTitle,
          notificationMessage: intl.get(
            'SCHEDULE.EVENTS.ERROR_NOTIFICATION_MESSAGE'
          ),
        })
      );
    },
    [dispatch]
  );

  const actionBegin = useCallback(
    async (args: ActionEventArgs) => {
      if (args.requestType === 'eventChange' && args?.changedRecords?.length) {
        try {
          if (args?.data?.hasOwnProperty('occurrence')) {
            const isRecurringEvent = true;
            let modifiedEventType: string = isNaN(Number(eventType))
              ? getEventTypeValueFromCode(eventType)
              : eventType;
            const data = args.changedRecords[0] as EventData;
            const parent = get(args, 'data.parent', null);
            let startTime = data.StartTime;
            let endTime = data.EndTime;
            let isOldEvent = data.isOldEvent ? false : data.isOldEvent;

            if (eventTimezones) {
              const isSingleDayEvent = isSingleDayAllDayEvent(data);
              const result = processEventTimesForEdit(
                data,
                isSingleDayEvent,
                isRecurringEvent
              );
              startTime = result.startTime;
              endTime = result.endTime;
            }

            if (parent) {
              let actionDispatched = false;
              const t = setInterval(async () => {
                if (parent.RecurrenceException) {
                  const exceptionEvent = getEventData({
                    projectIds,
                    eventTitle,
                    eventDescription,
                    eventType: modifiedEventType,
                    data: {
                      ...data,
                      isOldEvent,
                      StartTime: startTime,
                      EndTime: endTime,
                      ...(eventTimezones && {
                        CreatorTimeZone:
                          Intl.DateTimeFormat().resolvedOptions().timeZone,
                      }),
                    },
                    facilitators: selectedFacilitatorsIds,
                  });
                  const serializedException = serializeCreateException(
                    exceptionEvent,
                    parent.Id,
                    parent.RecurrenceException
                  );
                  try {
                    if (!actionDispatched) {
                      actionDispatched = true;
                      await dispatch(
                        createException({
                          scheduleId: parent.Id,
                          data: serializedException,
                        })
                      );
                    }
                  } catch {
                    dispatchErrorNotification(
                      intl.get('SCHEDULE.EVENTS.EDIT_ERROR_NOTIFICATION_TITLE')
                    );
                  }
                  clearInterval(t);
                }
              }, 10);
            }
          } else {
            const data = args.changedRecords[0] as EventData;
            let shouldResetExceptions: boolean = false;
            if (data) {
              let actionDispatched = false;
              const t = setInterval(async () => {
                if (
                  isEditingSeriesWithExceptions &&
                  !data.RecurrenceException
                ) {
                  shouldResetExceptions = true;
                }
                let modifiedEventType: string = isNaN(Number(eventType))
                  ? getEventTypeValueFromCode(eventType)
                  : eventType;
                let startTime = data.StartTime;
                let endTime = data.EndTime;
                let isOldEvent = data.isOldEvent ? false : data.isOldEvent;

                if (eventTimezones) {
                  const isSingleDayEvent = isSingleDayAllDayEvent(data);
                  const result = processEventTimesForEdit(
                    data,
                    isSingleDayEvent
                  );
                  startTime = result.startTime;
                  endTime = result.endTime;
                }

                const editedEvent = getEventData({
                  projectIds,
                  eventTitle,
                  eventDescription,
                  eventType: modifiedEventType,
                  data: {
                    ...data,
                    RecurrenceRuleFriendlyName:
                      generateReadableRecurrancePattern(data.RecurrenceRule),
                    OldRecurrenceRuleFriendlyName:
                      generateReadableRecurrancePattern(
                        (args.data as EventData)?.RecurrenceRule
                      ),
                    StartTime: startTime,
                    EndTime: endTime,
                    isOldEvent,
                    ...(eventTimezones && {
                      CreatorTimeZone:
                        Intl.DateTimeFormat().resolvedOptions().timeZone,
                    }),
                  },
                  facilitators: selectedFacilitatorsIds,
                });
                const editEventParams = serializeUpdateEvent(
                  editedEvent,
                  shouldResetExceptions
                );
                try {
                  if (!actionDispatched) {
                    actionDispatched = true;
                    await dispatch(
                      editEvent({
                        ...editEventParams,
                      })
                    );
                  }
                } catch {
                  dispatchErrorNotification(
                    intl.get('SCHEDULE.EVENTS.EDIT_ERROR_NOTIFICATION_TITLE')
                  );
                }
                clearInterval(t);
              }, 10);
            }
          }

          dispatch(
            showNotification({
              notificationVariant: 'success',
              notificationTitle: intl.get('SCHEDULE.EVENTS.EDIT_SUCCESS'),
              autoHide: false,
            })
          );
        } catch {
          dispatchErrorNotification(
            intl.get('SCHEDULE.EVENTS.EDIT_ERROR_NOTIFICATION_TITLE')
          );
        }
      } else if (args.requestType === 'eventCreate') {
        if (args.data && args.data.length) {
          try {
            const data = args.data as EventData[];
            const isSingleDayEvent = isSingleDayAllDayEvent(data[0]);
            let startTime = data[0].StartTime;
            let endTime = data[0].EndTime;

            if (eventTimezones) {
              const result = processEventTimesForCreate(
                data[0] as isSingleDayEventType,
                isSingleDayEvent
              );
              startTime = result.startTime;
              endTime = result.endTime;
            }

            const newEvent = {
              StartTime: startTime,
              EndTime: endTime,
              RecurrenceRule: data[0].RecurrenceRule,
              RecurrenceRuleFriendlyName: generateReadableRecurrancePattern(
                data[0].RecurrenceRule
              ),
              Id: data[0].Id,
              ProjectId: projectIds,
              FacilitatorId: selectedFacilitatorsIds,
              Subject: eventTitle,
              Description: eventDescription || null,
              EventType: eventType,
              IsAllDay: data[0].IsAllDay,
              isOldEvent: false,
              ...(eventTimezones && {
                CreatorTimeZone:
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
              }),
            } as EventData;

            args.cancel = true;
            const createEventParams = serializeCreateEvent(newEvent);
            await dispatch(
              createEvent({
                ...createEventParams,
              })
            );
            dispatch(
              showNotification({
                notificationVariant: 'success',
                notificationTitle: intl.get('SCHEDULE.EVENTS.CREATE_SUCCESS'),
                autoHide: false,
              })
            );
            resetStateHandler?.();
            // Close the editor modal after successful event creation
            if (scheduleObj?.current && scheduleObj.current.closeEditor) {
              scheduleObj.current.closeEditor();
            }
          } catch {
            dispatchErrorNotification(
              intl.get('SCHEDULE.EVENTS.CREATE_ERROR_NOTIFICATION_TITLE')
            );
          }
        }
      } else if (args.requestType === 'eventRemove') {
        try {
          const data = args.data;
          const changedRecords = args.changedRecords;

          if (Array.isArray(data) && data.length > 0) {
            const parent = data[0].parent;
            const occurrence = data[0].occurrence;
            if (parent && occurrence && parent.Id === occurrence.Id) {
              let startTime = data[0].parent.StartTime;
              let endTime = data[0].parent.EndTime;
              let isOldEvent = data[0].parent.isOldEvent
                ? false
                : data[0].parent.isOldEvent;

              if (eventTimezones) {
                startTime = new Date(startTime);
                endTime = new Date(endTime);
              }

              let actionDispatched = false;
              const t = setInterval(async () => {
                if (parent.RecurrenceException) {
                  args.cancel = true;
                  const modifiedEventType: string = getEventTypeValueFromCode(
                    parent.EventType
                  );
                  const editEventParams = serializeUpdateEvent({
                    ...parent,
                    isOldEvent,
                    EventType: modifiedEventType,
                    Facilitators: [],
                    StartTime: startTime,
                    EndTime: endTime,
                  });
                  try {
                    if (!actionDispatched) {
                      actionDispatched = true;
                      await dispatch(
                        editEvent({
                          ...editEventParams,
                        })
                      );
                    }
                  } catch {
                    dispatchErrorNotification(
                      intl.get('SCHEDULE.EVENTS.EDIT_ERROR_NOTIFICATION_TITLE')
                    );
                  }
                  clearInterval(t);
                }
              }, 10);
            } else {
              if (changedRecords && changedRecords.length) {
                await dispatch(deleteEvent(changedRecords[0].Id));
              } else {
                await dispatch(deleteEvent(data[0].Id));
              }
            }
          }
          dispatch(
            showNotification({
              notificationVariant: 'success',
              notificationTitle: intl.get('SCHEDULE.EVENTS.DELETE_SUCCESS'),
              autoHide: false,
            })
          );
        } catch {
          dispatchErrorNotification(
            intl.get('SCHEDULE.EVENTS.DELETE_ERROR_NOTIFICATION_TITLE')
          );
        }
      }
    },
    [
      eventTimezones,
      projectIds,
      eventTitle,
      eventDescription,
      eventType,
      selectedFacilitatorsIds,
      isEditingSeriesWithExceptions,
      resetStateHandler,
      scheduleObj,
      dispatch,
      dispatchErrorNotification,
    ]
  );

  return { actionBegin };
};
